import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useOrders, type OrderListItem } from "@/api/orders";
import { formatMsk } from "@/lib/datetime";
import { useT } from "@/lib/i18n";

const STATUS_BADGE: Record<number, string> = {
  0: "bg-slate-100 text-slate-700",
  10: "bg-amber-100 text-amber-800",
  2: "bg-emerald-100 text-emerald-800",
  7: "bg-red-100 text-red-700",
  5: "bg-slate-100 text-slate-500",
};

const STATUS_KEY: Record<number, string> = {
  0: "orders.status.new",
  10: "orders.status.in_progress",
  2: "orders.status.completed",
  7: "orders.status.refunded",
  5: "orders.status.canceled",
};

const PAGE = 25;

export function OrderHistory() {
  const navigate = useNavigate();
  const t = useT();
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<number | "">("");
  const [customId, setCustomId] = useState("");
  const [accountId, setAccountId] = useState("");

  const offset = page * PAGE;
  const orders = useOrders({
    offset,
    limit: PAGE,
    status: statusFilter === "" ? undefined : statusFilter,
    custom_id: customId.trim() || undefined,
    account_id: accountId.trim() || undefined,
  });

  const totalPages = orders.data
    ? Math.max(1, Math.ceil(orders.data.total / PAGE))
    : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">{t("orders.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("orders.subtitle")}</p>
      </div>

      <div className="card flex flex-wrap items-end gap-3 p-4">
        <div>
          <label className="label">{t("orders.filter.status")}</label>
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => {
              setPage(0);
              setStatusFilter(
                e.target.value === "" ? "" : Number(e.target.value),
              );
            }}
          >
            <option value="">{t("orders.status.all")}</option>
            <option value={0}>{t("orders.status.new")}</option>
            <option value={10}>{t("orders.status.in_progress")}</option>
            <option value={2}>{t("orders.status.completed")}</option>
            <option value={7}>{t("orders.status.refunded")}</option>
            <option value={5}>{t("orders.status.canceled")}</option>
          </select>
        </div>
        <div className="flex-1 min-w-[16rem]">
          <label className="label">{t("orders.filter.custom_id")}</label>
          <input
            type="text"
            className="input"
            placeholder={t("orders.filter.custom_id_placeholder")}
            value={customId}
            onChange={(e) => {
              setPage(0);
              setCustomId(e.target.value);
            }}
          />
        </div>
        <div className="flex-1 min-w-[16rem]">
          <label className="label">{t("orders.filter.account_id")}</label>
          <input
            type="text"
            className="input"
            placeholder={t("orders.filter.account_id_placeholder")}
            value={accountId}
            onChange={(e) => {
              setPage(0);
              setAccountId(e.target.value);
            }}
          />
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-surface-muted text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-3 font-semibold sm:px-4">{t("orders.col.date")}</th>
              <th className="px-3 py-3 font-semibold sm:px-4">{t("orders.col.product")}</th>
              <th className="hidden px-3 py-3 font-semibold sm:table-cell sm:px-4">
                {t("orders.col.qty")}
              </th>
              <th className="px-3 py-3 font-semibold sm:px-4">{t("orders.col.total")}</th>
              <th className="px-3 py-3 font-semibold sm:px-4">{t("orders.col.status")}</th>
              <th className="hidden px-3 py-3 font-semibold md:table-cell md:px-4">
                {t("orders.col.custom_id")}
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-400">
                  {t("orders.loading")}
                </td>
              </tr>
            )}
            {orders.data?.items.map((o: OrderListItem) => {
              const label = STATUS_KEY[o.status] ? t(STATUS_KEY[o.status]) : `status ${o.status}`;
              const badge = STATUS_BADGE[o.status] ?? "bg-slate-100 text-slate-700";
              const open = () =>
                navigate(`/orders/${encodeURIComponent(o.custom_id)}`);
              return (
                <tr
                  key={o.custom_id}
                  onClick={open}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      open();
                    }
                  }}
                  role="link"
                  tabIndex={0}
                  className="cursor-pointer border-t border-surface-border hover:bg-surface-muted focus:bg-surface-muted focus:outline-none"
                >
                  <td className="px-3 py-3 text-slate-600 sm:px-4">
                    {formatMsk(o.date)}
                  </td>
                  <td className="px-3 py-3 text-slate-800 sm:px-4">
                    {o.account_id ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-blue-800">
                          {t("orders.steam_topup_label")}
                        </span>
                        <span className="font-mono text-xs text-slate-700">
                          → {o.account_id}
                        </span>
                      </div>
                    ) : (
                      o.product ?? "-"
                    )}
                  </td>
                  <td className="hidden px-3 py-3 text-slate-700 sm:table-cell sm:px-4">
                    {o.quantity ?? "-"}
                  </td>
                  <td className="px-3 py-3 text-slate-700 sm:px-4">
                    {o.total_price !== null && o.total_price !== undefined
                      ? `${o.total_price.toFixed(4)}`
                      : "-"}
                  </td>
                  <td className="px-3 py-3 sm:px-4">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${badge}`}>
                      {label}
                    </span>
                  </td>
                  <td className="hidden px-3 py-3 font-mono text-xs text-slate-500 md:table-cell md:px-4">
                    {o.custom_id}
                  </td>
                </tr>
              );
            })}
            {orders.data && orders.data.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-400">
                  {t("orders.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {orders.data && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">
            {t("orders.page")} {page + 1} {t("orders.page.of")} {totalPages}
            {" · "}
            {orders.data.total} {t("orders.page.total")}
          </div>
          <div className="flex gap-2">
            <button
              className="btn btn-ghost text-xs"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              {t("orders.page.prev")}
            </button>
            <button
              className="btn btn-ghost text-xs"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("orders.page.next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
